import { AfterViewInit, Component, HostListener, OnInit, signal, inject } from '@angular/core';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import {
  ChallengeEventType,
  LimitedTimeEvent,
  LimitedTimeEventType,
  PendingToRedeemType,
} from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { NativeService } from '@shared/services/native.service';
import { Observable } from 'rxjs';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { TooltipBubbleComponent } from '@app/shared/components/tooltip-bubble/tooltip-bubble.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-multievents-modal',
  templateUrl: './multievents.component.html',
  styleUrls: ['./multievents.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    AnimatedTimerComponent,
    BadgeComponent,
    BoxComponent,
    TooltipBubbleComponent,
    SharedModule,
  ],
})
export class MultieventsModalComponent implements OnInit, AfterViewInit {
  private eventManager = inject(EventManager);
  logger = inject(LoggerService);
  private eventsRepo = inject(EventsRepository);
  private translateService = inject(TranslateService);
  private limitedTimeEventService = inject(LimitedTimeEventService);
  private nativeService = inject(NativeService);

  @HostListener('window:resize') onResizeWindow() {
    this.checkArrows();
  }

  componentDestroy: () => Observable<unknown>;

  limitedTimeEvents = signal<LimitedTimeEvent[]>([]);
  limitedTimeEventNames: string[] = [];

  lang: string;
  showArrows = signal(false);
  allEventsAreHidden = signal(false);
  LimitedTimeEventType = LimitedTimeEventType;
  gameChallengesToRedeemById: PendingToRedeemType[] = [];

  get multiEventsElementSelector() {
    return document.querySelector('.c-multievents__events');
  }

  ngAfterViewInit(): void {
    this.checkArrows();
  }

  ngOnInit(): void {
    this.lang = this.translateService.currentLang;

    this.eventsRepo.multiEventsLimitedTimeEvents$.pipe(takeUntil(this.componentDestroy())).subscribe((events) => {
      this.limitedTimeEvents.set(events);
      this.allEventsAreHidden.set(this.eventsRepo.hasAllEventsHidden());

      this.unshiftGameEvents();
      this.calculatePendingChallengesToRedeemById();
      this.manageEventsName();
      this.checkArrows();
    });
  }

  openLimitedTimeEvent(event: LimitedTimeEvent): void {
    this.limitedTimeEventService.manageOpenLimitedTimeEvents(event);
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.MultieventsModalComponent, ModalType.View));
  }

  onExpiredLimitedTimeEvent(event: LimitedTimeEvent) {
    this.limitedTimeEventService.removeLimitedTimeEvent(event.eventId);
  }

  private manageEventsName(): void {
    this.limitedTimeEvents()
      ?.filter((e) => !!LimitedTimeEventType[e.type])
      .forEach((e) => {
        if (e.type === LimitedTimeEventType.GameChallenges) {
          switch (e.properties.challengeeventtype) {
            case ChallengeEventType.Multi:
              this.limitedTimeEventNames.push('multi' + LimitedTimeEventType[e.type].toLowerCase());
              break;

            default:
              this.limitedTimeEventNames.push(LimitedTimeEventType[e.type].toLowerCase());
              break;
          }
        } else {
          this.limitedTimeEventNames.push(LimitedTimeEventType[e.type].toLowerCase());
        }
      });
  }

  private checkArrows(): void {
    if (this.nativeService.isDesktop()) {
      // Timeout for avoid NG0100 error
      setTimeout(() => {
        this.showArrows.set(
          this.multiEventsElementSelector && this.multiEventsElementSelector.clientWidth > document.body.clientWidth
        );
      }, 0);
    }
  }

  private unshiftGameEvents(): void {
    this.limitedTimeEvents.set(this.limitedTimeEventService.unshiftChallengeEvents(this.limitedTimeEvents()));
  }

  private calculatePendingChallengesToRedeemById(): void {
    this.gameChallengesToRedeemById = this.limitedTimeEventService.getPendingGameChallengesToRedeemById(
      this.limitedTimeEvents()
    );
  }
}
